import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
  useAdminSubawardeeFilterStore,
} from '../stores/adminSubawardeeFilterStore';
import { isNonEmptySubawardeeId } from '../utils/adminSubawardeeUtils';
import {
  useAdminMilestoneViewStore,
  type AdminMilestoneView,
} from '../stores/adminMilestoneViewStore';
import { useAdminComponentProjectFilterStore } from '../stores/adminComponentProjectFilterStore';
import { isKnownComponentProject } from '../config/componentProjects';
import { isAdminMilestonesRoute } from '../utils/adminSubawardeePaths';

function parseViewParam(value: string | null): AdminMilestoneView {
  return value === 'component' ? 'component' : 'subawardee';
}

export interface AdminMilestoneViewState {
  view: AdminMilestoneView;
  setView: (view: AdminMilestoneView) => void;
  /**
   * Effective component project for milestones queries.
   * Prefer navbar selection when set; otherwise page-local filter / URL.
   */
  componentProject: string | null;
  /** True when navbar has no project — page may show its own component-project filter. */
  showPageComponentProjectFilter: boolean;
  /** Updates page-local / URL scope only; never writes the navbar store. */
  setComponentProject: (value: string | null) => void;
  subawardee: string | null;
  setSubawardee: (value: string | null) => void;
}

/**
 * Milestones admin scope: view mode (component vs sub-awardee), persisted and synced with URL on `/admin/milestones`.
 * Navbar component project is read-only here; page filter changes do not overwrite it.
 * Sub-awardee comes from the shared admin sub-awardee filter store.
 */
export function useAdminMilestoneViewUrlState(): AdminMilestoneViewState {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const view = useAdminMilestoneViewStore((s) => s.view);
  const setViewStore = useAdminMilestoneViewStore((s) => s.setView);
  const navbarComponentProject = useAdminComponentProjectFilterStore((s) => s.componentProject);
  const [pageComponentProject, setPageComponentProject] = useState<string | null>(null);

  const componentProject = navbarComponentProject ?? pageComponentProject;
  const showPageComponentProjectFilter = !navbarComponentProject;

  const subawardee = useAdminSubawardeeFilterStore((s) => s.subawardee);
  const setSubawardeeStore = useAdminSubawardeeFilterStore((s) => s.setSubawardee);

  const onMilestonesRoute = isAdminMilestonesRoute(location.pathname);
  const lastUrlProjectRef = useRef<string | null>(null);

  // Deep link / browser history → page-local project (never navbar) + view/subawardee stores.
  useEffect(() => {
    if (!onMilestonesRoute) return;

    const viewFromUrl = parseViewParam(searchParams.get('view'));
    const hasSubawardeeParam = isNonEmptySubawardeeId(searchParams.get('subawardee'));
    const resolvedView =
      searchParams.get('view') == null && hasSubawardeeParam ? 'subawardee' : viewFromUrl;

    if (resolvedView !== view) {
      setViewStore(resolvedView);
    }

    const projectFromUrl = searchParams.get('component_project');
    if (
      isKnownComponentProject(projectFromUrl) &&
      projectFromUrl !== lastUrlProjectRef.current
    ) {
      lastUrlProjectRef.current = projectFromUrl;
      if (projectFromUrl !== pageComponentProject) {
        setPageComponentProject(projectFromUrl);
      }
    } else if (!isKnownComponentProject(projectFromUrl)) {
      lastUrlProjectRef.current = null;
    }

    const subFromUrl = searchParams.get('subawardee');
    if (isNonEmptySubawardeeId(subFromUrl) && subFromUrl !== subawardee) {
      setSubawardeeStore(subFromUrl);
    }
  }, [
    searchParams,
    onMilestonesRoute,
    view,
    pageComponentProject,
    subawardee,
    setViewStore,
    setSubawardeeStore,
  ]);

  const syncUrl = useCallback(
    (next: {
      view: AdminMilestoneView;
      subawardee: string | null;
      pageComponentProject: string | null;
      navbarComponentProject: string | null;
    }) => {
      if (!onMilestonesRoute) return;
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set('view', next.view);
          if (
            next.view === 'component' &&
            !next.navbarComponentProject &&
            isKnownComponentProject(next.pageComponentProject)
          ) {
            params.set('component_project', next.pageComponentProject);
          } else {
            params.delete('component_project');
          }
          if (next.view === 'subawardee') {
            if (next.subawardee && isNonEmptySubawardeeId(next.subawardee)) {
              params.set('subawardee', next.subawardee);
            } else {
              params.delete('subawardee');
            }
          } else {
            params.delete('subawardee');
          }
          return params;
        },
        { replace: true }
      );
    },
    [onMilestonesRoute, setSearchParams]
  );

  // Store has selection but URL is missing params (e.g. navbar link)
  useEffect(() => {
    if (!onMilestonesRoute) return;
    const hasView = searchParams.has('view');
    // Component view is scoped with or without a project (null = all projects).
    const hasScope = view === 'component' || view === 'subawardee';
    if (hasView && hasScope) return;

    const shouldSyncComponent = view === 'component';
    const shouldSyncSubawardee = view === 'subawardee' && isNonEmptySubawardeeId(subawardee);
    if (!shouldSyncComponent && !shouldSyncSubawardee && hasView) return;

    syncUrl({
      view,
      subawardee,
      pageComponentProject,
      navbarComponentProject,
    });
  }, [
    view,
    componentProject,
    pageComponentProject,
    navbarComponentProject,
    subawardee,
    onMilestonesRoute,
    searchParams,
    syncUrl,
  ]);

  const setView = useCallback(
    (value: AdminMilestoneView) => {
      setViewStore(value);
      syncUrl({
        view: value,
        subawardee,
        pageComponentProject,
        navbarComponentProject,
      });
    },
    [setViewStore, syncUrl, subawardee, pageComponentProject, navbarComponentProject]
  );

  const setComponentProject = useCallback(
    (value: string | null) => {
      if (value !== null && !isKnownComponentProject(value)) return;
      setPageComponentProject(value);
      syncUrl({
        view,
        subawardee,
        pageComponentProject: value,
        navbarComponentProject,
      });
    },
    [syncUrl, view, subawardee, navbarComponentProject]
  );

  const setSubawardee = useCallback(
    (value: string | null) => {
      setSubawardeeStore(value);
      const nextView = value ? 'subawardee' : view;
      if (value) setViewStore('subawardee');
      syncUrl({
        view: nextView,
        subawardee: value,
        pageComponentProject,
        navbarComponentProject,
      });
    },
    [
      setSubawardeeStore,
      setViewStore,
      syncUrl,
      view,
      pageComponentProject,
      navbarComponentProject,
    ]
  );

  return {
    view,
    setView,
    componentProject,
    showPageComponentProjectFilter,
    setComponentProject,
    subawardee,
    setSubawardee,
  };
}
